import { Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemText, SvgIcon, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { Aircraft, Airline, InflightEntertainment, Oven } from '@aviation/cabinapp-airlinedata-sdk';
import { del, post, put } from 'aws-amplify/api';
import { toast } from 'react-toastify';
import { ulid } from 'ulidx';
import { ReactComponent as PencilIcon } from '../../icons/pencil.svg';
import { ReactComponent as TrashIcon } from '../../icons/trash.svg';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';

export type LookupKind = 'ife' | 'oven';
export type LookupItem = InflightEntertainment | Oven;

export interface ILookupDialogProps {
    open: boolean;
    kind: LookupKind;
    items: Array<LookupItem>;
    airlines: Array<Airline>;
    aircrafts: Array<Aircraft>;
    onClose: () => void;
    onChanged: () => void;
}

const MAX_USAGE_EXAMPLES = 3;

export const clientCodeLabel = (code: string, airlines: Array<Airline>) => {
    const airline = airlines.find(a => a.Code === code);
    return airline?.Name ? `${airline.Name} (${code})` : code;
};

const LookupDialog = (props: ILookupDialogProps) => {
    const { open, kind, items, airlines, aircrafts, onClose, onChanged } = props;
    const title = kind === 'ife' ? 'Inflight Entertainment' : 'Oven';
    const path = `/api/airline/${kind}`;
    const [editItem, setEditItem] = useState<LookupItem | undefined>(undefined);
    const [isNew, setIsNew] = useState(false);
    const [busy, setBusy] = useState(false);

    const clientCodes = airlines.map(a => a.Code ?? '').filter(c => c !== '');

    const usedBy = (id?: string) : Array<string> => {
        return aircrafts
            .filter(a => (kind === 'ife' ? a.Ife : a.Ovens)?.some(i => i.Id === id))
            .map(a => a.AcReg ?? '');
    };

    const handleAdd = () => {
        setIsNew(true);
        setEditItem({ Id: ulid().toString(), Name: '', ClientCodes: [] });
    };

    const handleEdit = (item: LookupItem) => {
        setIsNew(false);
        setEditItem({ ...item, ClientCodes: [...(item.ClientCodes ?? [])] });
    };

    const handleSave = async () => {
        if (!editItem?.Name?.trim()) {
            toast.error('Name is required.');
            return;
        }
        const payload: any = { Id: editItem.Id, Name: editItem.Name.trim(), ClientCodes: editItem.ClientCodes ?? [] };
        setBusy(true);
        try {
            if (isNew)
                await post({ path, apiName: 'api', options: { body: { ...payload } } }).response;
            else
                await put({ path: `${path}/${payload.Id}`, apiName: 'api', options: { body: { ...payload } } }).response;
            toast.success(`${title} saved successfully.`);
            setEditItem(undefined);
            onChanged();
        } catch (e) {
            console.error(e);
            toast.error(`An error occurred while saving ${title}.`);
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async (item: LookupItem) => {
        const usages = usedBy(item.Id);
        if (usages.length > 0) {
            const examples = usages.slice(0, MAX_USAGE_EXAMPLES).join(', ');
            const more = usages.length > MAX_USAGE_EXAMPLES ? ` and ${usages.length - MAX_USAGE_EXAMPLES} more` : '';
            toast.error(`"${item.Name}" is still in use by ${usages.length} aircraft (e.g. ${examples}${more}) and cannot be deleted.`);
            return;
        }
        if (!window.confirm(`Delete "${item.Name}"?`))
            return;

        setBusy(true);
        try {
            await del({ path: `${path}/${item.Id}`, apiName: 'api' }).response;
            toast.success(`${title} deleted successfully.`);
            onChanged();
        } catch (e) {
            console.error(e);
            toast.error(`An error occurred while deleting ${title}.`);
        } finally {
            setBusy(false);
        }
    };

    const sortedItems = [...items].sort((a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                {editItem ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField label='Name' size='small' fullWidth value={editItem.Name ?? ''} autoFocus
                            onChange={(e) => setEditItem({ ...editItem, Name: e.target.value })} />
                        <Autocomplete
                            multiple
                            fullWidth
                            disableCloseOnSelect
                            size='small'
                            options={clientCodes}
                            value={editItem.ClientCodes ?? []}
                            getOptionLabel={(c) => clientCodeLabel(c, airlines)}
                            onChange={(_, value) => setEditItem({ ...editItem, ClientCodes: value })}
                            renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option, index) => (
                                    <Chip label={option} size='small' {...getTagProps({ index })} />
                                ))
                            }
                            renderInput={(params) => <TextField {...params} label='Client Codes' />}
                        />
                    </Box>
                ) : (
                    <>
                        {sortedItems.length === 0 && <Typography variant='body2'>No entries available.</Typography>}
                        <List dense>
                            {sortedItems.map(item => (
                                <ListItem key={item.Id} disableGutters secondaryAction={
                                    <>
                                        <IconButton disabled={busy} onClick={() => handleEdit(item)}>
                                            <SvgIcon component={PencilIcon} inheritViewBox />
                                        </IconButton>
                                        <IconButton disabled={busy} color='error' onClick={() => handleDelete(item)}>
                                            <SvgIcon component={TrashIcon} inheritViewBox />
                                        </IconButton>
                                    </>
                                }>
                                    <ListItemText primary={item.Name} secondary={(item.ClientCodes ?? []).join(', ')} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {busy && <CircularProgress size={19} />}
                {editItem ? (
                    <>
                        <Button onClick={() => setEditItem(undefined)} disabled={busy}>Cancel</Button>
                        <Button variant='contained' onClick={handleSave} disabled={busy}>Save</Button>
                    </>
                ) : (
                    <>
                        <Button startIcon={<SvgIcon component={PlusIcon} inheritViewBox />} onClick={handleAdd}>Add</Button>
                        <Button variant='contained' onClick={onClose}>Close</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LookupDialog;
